/**
 * Manual Linking Banner Component
 * Allows Order Desk users to manually link orders to previous Tax Search orders
 * for data prefill functionality.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../ui/dialog';
import { formatDateOnly } from '../../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

// Format date helper
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return formatDateOnly(dateString);
  } catch {
    return dateString;
  }
};

export const ManualLinkingBanner = ({ orderData }) => {
  const [previousOrder, setPreviousOrder] = useState(null);
  const [isLinking, setIsLinking] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    fetchPreviousOrder();
  }, [orderData.id]);

  const fetchPreviousOrder = async () => {
    try {
      const response = await axios.get(`${API}/orders/${orderData.id}/previous`);
      if (response.data.previous_order_id) {
        setPreviousOrder(response.data);
      }
    } catch (error) {
      // 403 = role not permitted to view linked orders — expected, not an error.
      if (error.response?.status !== 403) console.error('Failed to fetch previous order:', error);
    }
  };

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Search for completed Tax Search/Update Tax Search orders
      const response = await axios.get(`${API}/orders`, { 
        params: { 
          search: query,
          client_product: 'Tax Search,Update Tax Search',
          status: 'complete',
          limit: 10
        }
      });
      setSearchResults(response.data.orders || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLink = async (targetOrderId) => {
    setIsLinking(true);
    try {
      await axios.post(`${API}/orders/${orderData.id}/link-previous`, {
        previous_order_id: targetOrderId
      });
      
      toast.success('Orders linked successfully');
      setShowLinkDialog(false);
      setSearchQuery('');
      setSearchResults([]);
      await fetchPreviousOrder();
    } catch (error) {
      console.error('Link failed:', error);
      toast.error('Failed to link orders');
    } finally {
      setIsLinking(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm('This will unlink the previous order and disable auto-linking. Continue?')) {
      return;
    }

    setIsLinking(true);
    try {
      await axios.post(`${API}/orders/${orderData.id}/unlink-previous`);
      
      toast.success('Previous order unlinked');
      setPreviousOrder(null);
    } catch (error) {
      console.error('Unlink failed:', error);
      toast.error('Failed to unlink orders');
    } finally {
      setIsLinking(false);
    }
  };

  const handleReapplyData = async () => {
    if (!window.confirm('This will overwrite current changes with data from the previous order. Continue?')) {
      return;
    }

    try {
      await axios.post(`${API}/orders/${orderData.id}/tax-cert/prefill`);
      toast.success('Data re-applied from previous order');
    } catch (error) {
      console.error('Re-apply failed:', error);
      toast.error('Failed to re-apply data');
    }
  };

  return (
    <div className="bg-white border-b border-slate-200 px-6 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.102m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-slate-900">Previous Order Linking</h4>
            <p className="text-xs text-slate-600">
              {previousOrder 
                ? `Linked to ${previousOrder.order_number} (${previousOrder.client_product})`
                : 'No previous order linked'
              }
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {previousOrder ? (
            <>
              <Button
                size="sm"
                variant="outline" 
                onClick={handleReapplyData}
                className="text-xs"
              >
                Re-apply data
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleUnlink}
                disabled={isLinking}
                className="text-red-600 border-red-300 hover:bg-red-50 text-xs"
              >
                {isLinking ? 'Unlinking...' : 'Unlink'}
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowLinkDialog(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
            >
              Link previous order
            </Button>
          )}
        </div>
      </div>

      {/* Link Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Link Previous Order</DialogTitle>
            <DialogDescription>
              Search for a completed Tax Search or Update Tax Search order to link.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Input
                type="text"
                placeholder="Search by order number, address, or APN..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  handleSearch(e.target.value);
                }}
                className="pr-10"
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            
            {searchResults.length > 0 && (
              <div className="max-h-60 overflow-y-auto space-y-2">
                {searchResults.map((order) => (
                  <div 
                    key={order.id}
                    className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50"
                  >
                    <div>
                      <p className="text-sm font-medium">{order.order_number}</p>
                      <p className="text-xs text-slate-600">
                        {order.client_product} • {order.status} • {formatDate(order.created_at)}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleLink(order.id)}
                      disabled={isLinking}
                      className="text-xs"
                    >
                      Link
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            {searchQuery && searchResults.length === 0 && !isSearching && (
              <p className="text-sm text-slate-500 text-center py-4">
                No matching orders found. Try searching by order number or property details.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManualLinkingBanner;
